import React, { useEffect, useRef, useState } from "react";
import "./YouPayLine.css";
import { t } from "../i18n";
import { T } from "../i18n/T";

// "You pay: $14.50 (incl. fees)" — the fee-inclusive total, shown BEFORE the
// fan ever taps a pay button (founder decision). The "(incl. fees)" note is
// dot-underlined and tappable: one sentence explains the difference, tap
// anywhere closes it.
//
// Popup copy (founder trims): ONE sentence — what the fee is
// for, nothing else. No money-split claims ("100%", "we keep nothing"):
// the talent's settled share is exactly 85% and anything stronger is an
// over-claim we'd have to defend.

const YouPayLine: React.FC<{
  /** Already formatted in the DJ's own currency by the caller. */
  totalLabel: string;
  className?: string;
  /** "panel": YOU PAY and the number in one weight/size, the number is the
      tap target, no "(incl. fees)" note (tip and playlist checkouts). */
  variant?: "panel";
}> = ({ totalLabel, className, variant }) => {
  const [open, setOpen] = useState(false);
  // The bubble prefers to sit ABOVE the line; when the line is scrolled near
  // the top of the viewport it flips below instead of clipping off-screen.
  const [below, setBelow] = useState(false);
  const wrapRef = useRef<HTMLSpanElement | null>(null);

  // Tap-away close. `click` on document fires after the note's own onClick
  // (which stops propagation), so an open popup closes on any other tap.
  useEffect(() => {
    if (!open) return;
    const close = () => setOpen(false);
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [open]);

  const toggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    const top = wrapRef.current?.getBoundingClientRect().top ?? 200;
    setBelow(top < 130);
    setOpen((v) => !v);
  };
  const pop = open && (
    <span className={`ypl-pop${below ? " ypl-pop--below" : ""}`} role="note">
      {t("youPayLine.theDifferenceCoversCard")}
    </span>
  );

  if (variant === "panel") {
    return (
      <div className={`rq-you-pay rq-you-pay--panel${className ? ` ${className}` : ""}`}>
        {t("youPayLine.youPay")}
        <span className="ypl-note-wrap" ref={wrapRef}>
          {pop}
          <button
            type="button"
            className="ypl-amount"
            aria-expanded={open}
            aria-label={t("youPayLine.whatAreTheFees2", { totalLabel })}
            onClick={toggle}
          >
            {totalLabel}
          </button>
        </span>
      </div>
    );
  }

  return (
    <div className={`rq-you-pay${className ? ` ${className}` : ""}`}>
      <T k="youPayLine.youPay2" vars={{ totalLabel }} tags={{ 0: (c) => <span className="rq-you-pay-amount">{c}</span> }} />
      <span className="ypl-note-wrap" ref={wrapRef}>
        {pop}
        <button
          type="button"
          className="ypl-note"
          aria-expanded={open}
          aria-label={t("youPayLine.whatAreTheFees")}
          onClick={toggle}
        >
          {t("youPayLine.inclFees")}
        </button>
      </span>
    </div>
  );
};

export default YouPayLine;
